import { useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ClassroomDetail } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { SearchSelect } from '../../shared/ui/SearchSelect';
import { useAsync } from '../../shared/ui/useAsync';
import { toLocalInput } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { StandingsTab } from './Grades';
import { QuizzesTab } from './QuizzesTab';
import { ageRange, shortDate } from './shared';

type Run = (action: () => Promise<unknown>, ok?: string) => Promise<boolean>;
/** Tres pestañas: lo de cada clase va dentro de su clase; avisos arriba de Clases. */
const TABS = [
  ['clases', 'Clases'],
  ['temario', 'Temario'],
  ['alumnos', 'Alumnos'],
  ['posiciones', 'Posiciones'],
] as const;
type Tab = (typeof TABS)[number][0];
/** Direcciones de antes (…?ver=materiales) siguen funcionando. */
const OLD_TABS: Record<string, Tab> = { materiales: 'temario', cuestionarios: 'temario', comunicados: 'clases' };

/** Un salón de Raíces: clases (con avisos), temario (con materiales y cuestionarios) y alumnos. */
export function ClassroomPage() {
  const { classroomId = '' } = useParams();
  const me = useMe();
  const navigate = useNavigate();
  const churchId = me.activeChurchId;
  const base = `/organization/churches/${churchId}/classrooms/${classroomId}`;
  const data = useAsync((signal) => (churchId ? api.get<ClassroomDetail>(base, signal) : Promise.resolve(null)), [base]);
  const [params, setParams] = useSearchParams();
  const asked = params.get('ver') ?? '';
  const tab = (TABS.find(([k]) => k === asked)?.[0] ?? OLD_TABS[asked] ?? 'clases') as Tab;
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [posting, setPosting] = useState(false);

  const run: Run = async (action, ok) => {
    setBusy(true);
    setFeedback(null);
    try {
      await action();
      if (ok) setFeedback({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const c = data.data;
  if (!c) return null;
  const ages = ageRange(c.minAge, c.maxAge);
  const go = (key: Tab) => setParams(key === 'clases' ? {} : { ver: key }, { replace: true });

  return (
    <div className={`page${c.ministry.kids ? ' kids' : ''}`}>
      <p className="small">
        <Link to="/app/salones">← {c.ministry.kids ? 'Raíces Kids' : 'Raíces'}</Link>
      </p>
      <header className="page__header page__header--actions">
        <div>
          <h1>{c.name}</h1>
          <p className="muted">
            {ages ? `${ages} · ` : ''}
            {c.studentCount} {c.studentCount === 1 ? 'alumno' : 'alumnos'}
            {c.teachers.length ? ` · Maestro: ${c.teachers.map((t) => t.name).join(', ')}` : ''}
          </p>
          {c.description ? <p className="small">{c.description}</p> : null}
          {c.canTeach ? (
            <p className="small">
              <Link to={`/app/salones/panel/${c.id}`}>Panel del maestro</Link>: clase de hoy, asistencia, puntos y balance de cada alumno.
            </p>
          ) : null}
        </div>
        {c.canTeach || c.canManage ? (
          <div className="room-menu">
            <Button variant="ghost" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
              ⋯ Más
            </Button>
            {menu ? (
              <MenuList>
                {c.canTeach ? (
                  <Button variant="ghost" role="menuitem" onClick={() => { setMenu(false); setPosting(true); go('clases'); }}>
                    Publicar aviso
                  </Button>
                ) : null}
                {c.canManage ? (
                  <Button variant="ghost" role="menuitem" onClick={() => { setMenu(false); setEditing(true); }}>
                    Editar salón
                  </Button>
                ) : null}
                {c.canManage ? (
                  <Button
                    variant="ghost"
                    role="menuitem"
                    disabled={busy}
                    onClick={() => {
                      setMenu(false);
                      if (window.confirm(`¿Borrar el salón ${c.name}? Se borran sus clases, temario, materiales y cuestionarios.`)) {
                        void run(() => api.delete(base)).then((ok) => ok && navigate(`/app/ministerios/${c.ministry.id}`));
                      }
                    }}
                  >
                    Borrar salón
                  </Button>
                ) : null}
              </MenuList>
            ) : null}
          </div>
        ) : null}
      </header>
      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}
      {editing ? <EditRoom c={c} base={base} run={run} busy={busy} onDone={() => setEditing(false)} /> : null}

      <div className="tabs" role="tablist" aria-label="Secciones del salón">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={`tabs__tab${tab === key ? ' tabs__tab--active' : ''}`} onClick={() => go(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'clases' ? <SessionsTab c={c} base={base} run={run} busy={busy} posting={posting} setPosting={setPosting} /> : null}
      {tab === 'temario' ? <TopicsTab c={c} base={base} run={run} busy={busy} /> : null}
      {tab === 'alumnos' ? <PeopleTab c={c} base={base} run={run} busy={busy} /> : null}
      {tab === 'posiciones' ? <StandingsTab base={base} /> : null}
    </div>
  );
}

type TabProps = { c: ClassroomDetail; base: string; run: Run; busy: boolean };
type Session = ClassroomDetail['sessions'][number];

/** «2 materiales · 1 cuestionario» de una clase. */
/** Menú de «⋯ Más»: se abre hacia el lado donde cabe, para que nunca quede cortado fuera de la pantalla. */
function MenuList({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState<'left' | 'right'>('left');
  useLayoutEffect(() => {
    const box = ref.current?.getBoundingClientRect();
    if (box && box.right > document.documentElement.clientWidth - 8) setSide('right');
  }, []);
  return (
    <div ref={ref} className={`room-menu__list room-menu__list--${side}`} role="menu">
      {children}
    </div>
  );
}

function extrasOf(c: ClassroomDetail, sessionId: string): string | null {
  const docs = c.documents.filter((d) => d.sessionId === sessionId).length;
  const quizzes = c.quizzes.filter((q) => q.sessionId === sessionId).length;
  const parts = [docs ? `${docs} ${docs === 1 ? 'material' : 'materiales'}` : null, quizzes ? `${quizzes} ${quizzes === 1 ? 'cuestionario' : 'cuestionarios'}` : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

// ── Clases (con avisos) ─────────────────────────────────────────────────────

function SessionsTab({ c, base, run, busy, posting, setPosting }: TabProps & { posting: boolean; setPosting: (on: boolean) => void }) {
  const [scheduling, setScheduling] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const now = Date.now();
  const upcoming = c.sessions.filter((s) => new Date(s.endsAt).getTime() >= now);
  const past = c.sessions.filter((s) => new Date(s.endsAt).getTime() < now).reverse();
  const [next, ...rest] = upcoming;
  const row = (s: Session) => {
    const extras = extrasOf(c, s.id);
    return (
      <li key={s.id} className="list__item">
        <Link className="list__link list__link--row service-line" to={`/app/salones/${c.id}/clases/${s.id}`}>
          <span className="service-line__when">{shortDate(s.startsAt)}</span>
          <span className="service-line__title">
            <strong>{s.topic ? s.topic.title : 'Clase sin tema'}</strong>
            {extras ? <span className="small muted"> · {extras}</span> : null}
          </span>
          {s.canceled ? <span className="badge">Cancelada</span> : null}
        </Link>
      </li>
    );
  };
  return (
    <>
      <Notices c={c} base={base} run={run} busy={busy} posting={posting} setPosting={setPosting} />
      {next ? (
        <Card title="Próxima clase">
          <div className="next-service">
            <span className="service__when">{shortDate(next.startsAt)}</span>
            <strong className="next-service__title">{next.topic ? next.topic.title : 'Clase sin tema'}</strong>
            {extrasOf(c, next.id) ? <span className="small muted">{extrasOf(c, next.id)}</span> : null}
            {next.canceled ? <span className="badge">Cancelada</span> : null}
          </div>
          <Link className="btn btn--primary" to={`/app/salones/${c.id}/clases/${next.id}`}>
            Abrir clase
          </Link>
        </Card>
      ) : (
        <Card>
          <p className="muted">No hay clases programadas.</p>
        </Card>
      )}
      {rest.length ? (
        <Card title="Siguientes clases">
          <ul className="list">{rest.map(row)}</ul>
        </Card>
      ) : null}
      {past.length ? (
        <Card title={`Clases anteriores (${past.length})`}>
          <ul className="list">{(showPast ? past : past.slice(0, 3)).map(row)}</ul>
          {past.length > 3 ? (
            <Button variant="ghost" onClick={() => setShowPast((v) => !v)}>
              {showPast ? 'Ver solo las recientes' : `Ver todas (${past.length})`}
            </Button>
          ) : null}
        </Card>
      ) : null}
      {c.canTeach ? (
        scheduling ? (
          <ScheduleCard c={c} base={base} run={run} busy={busy} onDone={() => setScheduling(false)} />
        ) : (
          <Button variant="secondary" onClick={() => setScheduling(true)}>
            + Programar clases
          </Button>
        )
      ) : null}
    </>
  );
}

/** Avisos del salón: el último a la vista; los demás al tocar «Ver todos». */
function Notices({ c, base, run, busy, posting, setPosting }: TabProps & { posting: boolean; setPosting: (on: boolean) => void }) {
  const [all, setAll] = useState(false);
  const [body, setBody] = useState('');
  if (c.posts.length === 0 && !posting) return null;
  const shown = all ? c.posts : c.posts.slice(0, 1);
  return (
    <Card title={c.posts.length > 1 ? `Avisos (${c.posts.length})` : 'Aviso'}>
      {posting ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) void run(() => api.post(`${base}/posts`, { body: body.trim() }), 'Aviso publicado.').then((ok) => { if (ok) { setBody(''); setPosting(false); } });
          }}
        >
          <div className="field">
            <label htmlFor="post-new">Nuevo aviso</label>
            <textarea id="post-new" rows={3} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="El próximo domingo traigan su Biblia y cuaderno." />
          </div>
          <div className="actions">
            <Button type="submit" busy={busy} disabled={!body.trim()}>
              Publicar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setPosting(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
      <ul className="list">
        {shown.map((p) => (
          <li key={p.id} className="list__item list__item--actions">
            <span className="agenda__body">
              <span className="pre-line">{p.body}</span>
              <span className="muted small">
                {p.author.name} · {shortDate(p.createdAt)}
              </span>
            </span>
            {c.canTeach ? (
              <Button variant="ghost" disabled={busy} aria-label="Quitar aviso" onClick={() => void run(() => api.delete(`${base}/posts/${p.id}`))}>
                Quitar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {c.posts.length > 1 ? (
        <Button variant="ghost" onClick={() => setAll((a) => !a)}>
          {all ? 'Ver solo el último' : `Ver todos (${c.posts.length})`}
        </Button>
      ) : null}
    </Card>
  );
}

const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** Programar clases: fija (cada semana, mismo día y hora) o variable (una fecha). */
function ScheduleCard({ c, base, run, busy, onDone }: TabProps & { onDone: () => void }) {
  const [mode, setMode] = useState<'fija' | 'variable'>('fija');
  const next = new Date();
  next.setDate(next.getDate() + ((7 - next.getDay()) % 7 || 7));
  next.setHours(12, 0, 0, 0);
  const [first, setFirst] = useState(toLocalInput(next));
  const [weeks, setWeeks] = useState(Math.max(c.topics.length, 4));
  const [minutes, setMinutes] = useState(60);
  const [assign, setAssign] = useState(c.topics.length > 0);
  const [topicId, setTopicId] = useState('');
  const start = new Date(first);
  const valid = !Number.isNaN(start.getTime());

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    const count = mode === 'fija' ? Math.min(Math.max(weeks, 1), 60) : 1;
    const items = Array.from({ length: count }, (_, i) => {
      const s = new Date(start);
      s.setDate(s.getDate() + i * 7);
      return { startsAt: s.toISOString(), endsAt: new Date(s.getTime() + minutes * 60_000).toISOString(), topicId: mode === 'variable' && topicId ? topicId : null };
    });
    void run(() => api.post(`${base}/sessions`, { items, assignTopics: assign }), count === 1 ? 'Clase programada.' : `${count} clases programadas.`).then((ok) => ok && onDone());
  }

  return (
    <Card title="Programar clases">
      <form className="form" onSubmit={submit}>
        <div className="segmented" role="radiogroup" aria-label="Tipo de horario">
          <label className="check">
            <input type="radio" name="mode" checked={mode === 'fija'} onChange={() => setMode('fija')} /> Fija (cada semana)
          </label>
          <label className="check">
            <input type="radio" name="mode" checked={mode === 'variable'} onChange={() => setMode('variable')} /> Variable (una fecha)
          </label>
        </div>
        <div className="form__row">
          <Field label={mode === 'fija' ? 'Primera clase' : 'Fecha y hora'} type="datetime-local" value={first} onChange={(e) => setFirst(e.target.value)} required />
          <Field label="Duración (minutos)" type="number" min={15} max={600} value={minutes} onChange={(e) => setMinutes(Number(e.target.value) || 60)} />
        </div>
        {mode === 'fija' ? (
          <>
            <Field label="¿Cuántas semanas?" type="number" min={1} max={60} value={weeks} onChange={(e) => setWeeks(Number(e.target.value) || 1)} />
            {valid ? (
              <p className="small">
                Todos los {WEEKDAYS[start.getDay()]} a las {start.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}, {Math.min(Math.max(weeks, 1), 60)} semanas.
              </p>
            ) : null}
          </>
        ) : c.topics.length > 0 ? (
          <div className="field">
            <label htmlFor="sch-topic">Tema del temario</label>
            <select id="sch-topic" value={topicId} onChange={(e) => setTopicId(e.target.value)}>
              <option value="">El siguiente sin clase</option>
              {c.topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.position + 1}. {t.title}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {c.topics.length > 0 ? (
          <label className="check">
            <input type="checkbox" checked={assign} onChange={(e) => setAssign(e.target.checked)} /> Asignar los temas del temario en orden
          </label>
        ) : (
          <p className="small muted">Agrega el temario para asignar un tema a cada clase.</p>
        )}
        <div className="actions">
          <Button type="submit" busy={busy} disabled={!valid}>
            Programar
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ── Temario (con materiales y cuestionarios) ────────────────────────────────

function TopicsTab({ c, base, run, busy }: TabProps) {
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const sessionOf = (topicId: string) => c.sessions.find((s) => s.topic?.id === topicId && !s.canceled);
  return (
    <>
      <Card
        title={`Temario (${c.topics.length} ${c.topics.length === 1 ? 'tema' : 'temas'})`}
        action={
          c.canTeach ? (
            <Button variant="ghost" onClick={() => { setEditMode((m) => !m); setEditing(null); }}>
              {editMode ? 'Listo' : 'Editar temario'}
            </Button>
          ) : null
        }
      >
        {c.topics.length === 0 ? <p className="muted">Todavía no hay temario.</p> : null}
        <ol className="syllabus">
          {c.topics.map((t, i) => {
            const s = sessionOf(t.id);
            return (
              <li key={t.id} className="syllabus__item">
                {editing === t.id ? (
                  <form
                    className="inline-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run(() => api.put(`${base}/topics/${t.id}`, { title: title.trim(), description: t.description, position: i })).then((ok) => ok && setEditing(null));
                    }}
                  >
                    <label className="visually-hidden" htmlFor={`topic-${t.id}`}>
                      Tema
                    </label>
                    <input id={`topic-${t.id}`} value={title} maxLength={150} onChange={(e) => setTitle(e.target.value)} />
                    <Button type="submit" busy={busy} disabled={!title.trim()}>
                      Guardar
                    </Button>
                  </form>
                ) : (
                  <span className="agenda__body">
                    <strong>{t.title}</strong>
                    {s ? (
                      <Link className="small" to={`/app/salones/${c.id}/clases/${s.id}`}>
                        Clase: {shortDate(s.startsAt)}
                        {extrasOf(c, s.id) ? ` · ${extrasOf(c, s.id)}` : ''}
                      </Link>
                    ) : (
                      <span className="small muted">Sin clase asignada</span>
                    )}
                  </span>
                )}
                {editMode && editing !== t.id ? (
                  <span className="actions">
                    <Button variant="ghost" aria-label={`Editar ${t.title}`} onClick={() => { setEditing(t.id); setTitle(t.title); }}>
                      Editar
                    </Button>
                    <Button variant="ghost" disabled={busy} aria-label={`Quitar ${t.title}`} onClick={() => void run(() => api.delete(`${base}/topics/${t.id}`))}>
                      Quitar
                    </Button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
        {editMode || (c.canTeach && c.topics.length === 0) ? (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const items = draft.split('\n').map((l) => l.trim()).filter(Boolean).map((title) => ({ title }));
              if (items.length) void run(() => api.post(`${base}/topics`, { items }), 'Temario actualizado.').then((ok) => ok && setDraft(''));
            }}
          >
            <div className="field">
              <label htmlFor="topics-new">Agregar temas (uno por línea)</label>
              <textarea id="topics-new" rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={'La creación\nLa caída\nEl diluvio'} />
            </div>
            <Button type="submit" variant="secondary" busy={busy} disabled={!draft.trim()}>
              Agregar al temario
            </Button>
          </form>
        ) : null}
      </Card>
      <Materials c={c} base={base} run={run} busy={busy} />
      <section aria-label="Cuestionarios">
        <h2 className="section-title">Cuestionarios</h2>
        <QuizzesTab c={c} base={base} run={run} busy={busy} />
      </section>
    </>
  );
}

/** Materiales del salón (cada uno dice de qué clase es; en la clase se ven los suyos). */
function Materials({ c, base, run, busy }: TabProps) {
  const [adding, setAdding] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [sessionId, setSessionId] = useState('');
  const fileUrl = (id: string) => `/api${base}/documents/${id}/file`;
  const sessionTitle = (id: string | null) => {
    const s = id ? c.sessions.find((x) => x.id === id) : undefined;
    return s ? `${s.topic?.title ?? 'Clase'} · ${shortDate(s.startsAt)}` : null;
  };
  return (
    <Card
      title={`Materiales (${c.documents.length})`}
      action={
        c.canTeach && !adding ? (
          <Button variant="ghost" onClick={() => setAdding(true)}>
            + Subir material
          </Button>
        ) : null
      }
    >
      {c.documents.length === 0 && !adding ? <p className="muted">Todavía no hay materiales.</p> : null}
      <ul className="list">
        {c.documents.map((d) => (
          <li key={d.id} className="list__item list__item--actions">
            <a className="agenda__body" href={`${fileUrl(d.id)}?ver=1`} target="_blank" rel="noopener noreferrer">
              <strong>{d.title}</strong>
              <span className="muted small">
                {d.mime === 'application/pdf' ? 'PDF' : d.mime.split('/')[0] === 'image' ? 'Imagen' : d.mime.split('/')[0] === 'audio' ? 'Audio' : 'Video'}
                {' · '}
                {sessionTitle(d.sessionId) ?? 'Todo el salón'}
              </span>
            </a>
            <span className="actions">
              <a className="btn btn--ghost" href={fileUrl(d.id)} download aria-label={`Descargar ${d.title}`}>
                ⬇
              </a>
              {c.canTeach ? (
                <Button variant="ghost" disabled={busy} aria-label={`Quitar ${d.title}`} onClick={() => void run(() => api.delete(`${base}/documents/${d.id}`))}>
                  Quitar
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {adding ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!file) return;
            const headers: Record<string, string> = { 'X-Title': encodeURIComponent(title.trim()) };
            if (sessionId) headers['X-Session-Id'] = sessionId;
            void run(() => api.upload(`${base}/documents`, file, headers), 'Material subido.').then((ok) => {
              if (ok) {
                setFile(null);
                setTitle('');
                setAdding(false);
              }
            });
          }}
        >
          <div className="field">
            <label htmlFor="doc-file">Archivo (PDF, imagen, audio o video)</label>
            <input id="doc-file" type="file" accept="application/pdf,image/*,audio/*,video/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <Field label="Título (opcional)" value={title} maxLength={150} onChange={(e) => setTitle(e.target.value)} />
          {c.sessions.length > 0 ? (
            <div className="field">
              <label htmlFor="doc-session">Para la clase (opcional)</label>
              <select id="doc-session" value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                <option value="">Todo el salón</option>
                {c.sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.topic?.title ?? 'Clase'} · {shortDate(s.startsAt)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="actions">
            <Button type="submit" busy={busy} disabled={!file}>
              Subir material
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

// ── Alumnos y maestros ──────────────────────────────────────────────────────

/** Buscar y agregar (maestro, persona o ministerio). */
function PickAndAdd({ id, label, options, onAdd, busy }: { id: string; label: string; options: { id: string; name: string }[]; onAdd: (id: string) => void; busy: boolean }) {
  if (options.length === 0) return null;
  return (
    <form
      className="family-add"
      onSubmit={(e) => {
        e.preventDefault();
        const v = String(new FormData(e.currentTarget).get('pick') ?? '');
        if (v) onAdd(v);
      }}
    >
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <SearchSelect id={id} name="pick" options={options.map((o) => ({ value: o.id, label: o.name }))} />
      </div>
      <Button type="submit" busy={busy}>
        Agregar
      </Button>
    </form>
  );
}

function Chips({ items, onRemove, busy, label }: { items: { id: string; name: string }[]; onRemove?: ((id: string) => void) | undefined; busy: boolean; label: string }): ReactNode {
  if (items.length === 0) return <p className="muted small">Ninguno.</p>;
  return (
    <ul className="ev-chips">
      {items.map((i) => (
        <li key={i.id} className="ev-chip">
          {i.name}
          {onRemove ? (
            <button type="button" className="ev-chip__x" disabled={busy} aria-label={`Quitar a ${i.name} de ${label}`} onClick={() => onRemove(i.id)}>
              ×
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function PeopleTab({ c, base, run, busy }: TabProps) {
  const toOpt = (p: { personId: string; name: string }) => ({ id: p.personId, name: p.name });
  const teacherIds = new Set(c.teachers.map((t) => t.personId));
  const peopleIds = new Set(c.people.map((p) => p.personId));
  const [adding, setAdding] = useState(false);
  return (
    <>
      <Card title={c.teachers.length === 1 ? 'Maestro' : 'Maestros'}>
        <Chips items={c.teachers.map(toOpt)} busy={busy} label="los maestros" onRemove={c.canManage ? (id) => void run(() => api.delete(`${base}/teachers/${id}`)) : undefined} />
        {c.canManage ? (
          <PickAndAdd id="add-teacher" label="Nombrar maestro" options={c.candidates.filter((p) => !teacherIds.has(p.personId)).map(toOpt)} busy={busy} onAdd={(id) => void run(() => api.put(`${base}/teachers/${id}`, {}), 'Maestro nombrado.')} />
        ) : null}
      </Card>
      <Card
        title={`Alumnos (${c.students.length})`}
        action={
          c.canTeach && !adding ? (
            <Button variant="ghost" onClick={() => setAdding(true)}>
              + Agregar
            </Button>
          ) : null
        }
      >
        {c.minAge !== null || c.maxAge !== null ? (
          <p className="small muted">
            {c.ministry.kids ? 'Los niños del grupo Niños' : 'Las personas de Raíces'} de {ageRange(c.minAge, c.maxAge)} entran solas según su edad.
          </p>
        ) : null}
        {c.students.length === 0 ? (
          <p className="muted">Todavía no hay alumnos.</p>
        ) : (
          <ul className="student-list">
            {c.students.map((s) => (
              <li key={s.personId}>
                {s.name}
                {/* Solo los agregados a mano se quitan aquí; los que entran por edad o ministerio, no. */}
                {c.canTeach && peopleIds.has(s.personId) ? (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    aria-label={`Quitar a ${s.name} del salón`}
                    onClick={() => {
                      if (window.confirm(`¿Quitar a ${s.name} del salón?`)) void run(() => api.delete(`${base}/people/${s.personId}`), 'Alumno quitado.');
                    }}
                  >
                    Quitar
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {adding ? (
          <div className="room-add">
            <h3 className="small">Agregar a una persona</h3>
            <PickAndAdd
              id="add-person"
              label="Buscar persona"
              options={c.candidates.filter((p) => !peopleIds.has(p.personId) && !teacherIds.has(p.personId)).map(toOpt)}
              busy={busy}
              onAdd={(id) => void run(() => api.put(`${base}/people/${id}`, {}), 'Persona agregada.')}
            />
            <h3 className="small">Agregar varios: un ministerio completo</h3>
            <Chips items={c.ministries} busy={busy} label="los ministerios" onRemove={(id) => void run(() => api.delete(`${base}/ministries/${id}`))} />
            <PickAndAdd
              id="add-ministry"
              label="Agregar un ministerio completo"
              options={c.ministryOptions.filter((m) => !c.ministries.some((x) => x.id === m.id))}
              busy={busy}
              onAdd={(id) => void run(() => api.put(`${base}/ministries/${id}`, {}), 'Ministerio agregado.')}
            />
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Listo
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  );
}

function EditRoom({ c, base, run, busy, onDone }: TabProps & { onDone: () => void }) {
  return (
    <Card title="Editar salón">
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const num = (k: string) => (String(f.get(k) ?? '').trim() === '' ? null : Number(f.get(k)));
          void run(
            () => api.put(base, { name: String(f.get('name') ?? '').trim(), description: String(f.get('description') ?? '').trim() || null, minAge: num('minAge'), maxAge: num('maxAge') }),
            'Salón actualizado.',
          ).then((ok) => ok && onDone());
        }}
      >
        <Field label="Nombre del salón" name="name" required maxLength={80} defaultValue={c.name} />
        <Field label="Descripción" name="description" maxLength={1000} defaultValue={c.description ?? ''} />
        <div className="form__row">
          <Field label="Edad desde" name="minAge" type="number" min={0} max={120} defaultValue={c.minAge ?? ''} />
          <Field label="Edad hasta" name="maxAge" type="number" min={0} max={120} defaultValue={c.maxAge ?? ''} />
        </div>
        <p className="small muted">Con rango de edad, cada persona entra sola a este salón según su edad. Déjalo vacío para asignar a mano.</p>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
